import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import type {
  InternalHost,
  XingletComponent,
  XingletLoaderProps,
} from '@xingternal/crate-xinglet';

type InternalXingletLoaderProps = XingletLoaderProps & {
  host: InternalHost;
};

// todo: rewrite to use React.Suspense/React.lazy for better UX
// this will need a bit of extra-work since React.lazy is currently
// not very dynamic and could lead to memory-leaks if recreated during
// re-render
export const InternalXingletLoader: FC<InternalXingletLoaderProps> = ({
  host,
  name,
  fallback = null,
  ...rest
}) => {
  const [state, setState] = useState<{
    error?: unknown;
    Component?: XingletComponent;
  }>({});

  const ssr = host.runtime.metadata[name]?.ssr ?? false;
  const skipRendering = host.isServer && !ssr;
  const runtime = host.runtime;
  const eventualComponent = skipRendering
    ? undefined
    : runtime.loadXingletComponent(host, name);

  const error = eventualComponent?.error ?? state.error;
  const Component = eventualComponent?.value ?? state.Component;

  useEffect(() => {
    if (Boolean(Component) || Boolean(error) || !runtime.autoRerender) {
      return;
    }

    let mounted = true;

    eventualComponent?.then(
      (Component) => {
        if (!mounted) return;

        setState({ Component });
      },
      (error) => {
        if (!mounted) return;

        setState({ error });
      }
    );

    return () => {
      mounted = false;
    };
  }, [eventualComponent, runtime, error, Component]);

  useEffect(() => {
    return runtime.events.on('xinglet-reloaded', (otherName) => {
      if (otherName !== name || !Component) return;
      setState({});
    });
  }, [Component, runtime, name]);

  if (error) {
    if (error instanceof Error && !error.message.startsWith(`${name}:`)) {
      error.message = `${name}: ${error.message}`;
    }

    throw error;
  }

  if (!Component && !skipRendering) {
    runtime.events.emit('xinglet-awaited', name);
  }

  return Component ? <Component {...rest} /> : fallback;
};
